// Space Meteor Survival 30Hz Server Engine (Spectator Mode & Live Survival Rankings)
export class SpaceEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.width = 900;
    this.height = 600;
    this.ships = {};
    this.meteors = [];
    this.loopInterval = null;
    this.elapsed = 0;
    this.deathOrder = [];
  }

  start() {
    this.ships = {};
    this.meteors = [];
    this.elapsed = 0;
    this.deathOrder = [];

    const colors = ['#38bdf8', '#fbbf24', '#4ade80', '#f43f5e', '#a855f7', '#ec4899', '#f97316', '#06b6d4'];

    const players = [...this.room.players];
    // If solo player testing, add AI bot pilots!
    if (players.length === 1) {
      players.push(
        { id: 'bot_star', name: 'Nova Bot 🚀' },
        { id: 'bot_comet', name: 'Comet Bot 🚀' }
      );
    }

    players.forEach((p, idx) => {
      this.ships[p.id] = {
        id: p.id,
        name: p.name,
        color: colors[idx % colors.length],
        x: 200 + (idx * 110) % (this.width - 400),
        y: this.height / 2,
        vx: 0,
        vy: 0,
        survivalSeconds: 0,
        isAlive: true,
        isBot: !!p.isBot || p.id.startsWith('bot_')
      };
    });

    if (this.loopInterval) clearInterval(this.loopInterval);
    this.loopInterval = setInterval(() => this.update(), 1000 / 30);
  }

  handleInput(playerId, input) {
    const s = this.ships[playerId];
    if (s && s.isAlive) {
      const speed = 6.5;
      s.vx = (input.right ? speed : 0) - (input.left ? speed : 0);
      s.vy = (input.down ? speed : 0) - (input.up ? speed : 0);
    }
  }

  spawnMeteor() {
    const fromTop = Math.random() > 0.4;
    this.meteors.push({
      x: fromTop ? Math.random() * this.width : this.width + 20,
      y: fromTop ? -20 : Math.random() * this.height,
      vx: (Math.random() - 0.7) * 4.5 - 2,
      vy: Math.random() * 4.5 + 2,
      radius: 14 + Math.random() * 20
    });
  }

  update() {
    this.elapsed += 1 / 30;

    // Escalating spawn rate
    if (Math.random() < 0.16 + this.elapsed * 0.006) {
      this.spawnMeteor();
    }

    let aliveCount = 0;
    let lastAlive = null;

    // Update Ships
    for (const id in this.ships) {
      const s = this.ships[id];
      if (!s.isAlive) continue;

      aliveCount++;
      lastAlive = s;
      s.survivalSeconds = Math.floor(this.elapsed);

      // Bot evasion AI
      if (s.isBot) {
        let dodgeX = 0, dodgeY = 0;
        this.meteors.forEach((m) => {
          const d = Math.hypot(m.x - s.x, m.y - s.y);
          if (d < 90) {
            dodgeX += (s.x - m.x) * 0.08;
            dodgeY += (s.y - m.y) * 0.08;
          }
        });
        s.vx = Math.max(-5, Math.min(5, s.vx + dodgeX));
        s.vy = Math.max(-5, Math.min(5, s.vy + dodgeY));
      }

      s.x = Math.max(20, Math.min(this.width - 20, s.x + s.vx));
      s.y = Math.max(20, Math.min(this.height - 20, s.y + s.vy));
    }

    // Update Meteors & Collision Check
    for (let i = this.meteors.length - 1; i >= 0; i--) {
      const m = this.meteors[i];
      m.x += m.vx;
      m.y += m.vy;

      // Check collision
      for (const id in this.ships) {
        const s = this.ships[id];
        if (s.isAlive && Math.hypot(s.x - m.x, s.y - m.y) < m.radius + 13) {
          s.isAlive = false;
          s.survivalSeconds = Math.floor(this.elapsed);
          this.deathOrder.unshift(s);

          this.io.to(this.room.code).emit('space_shipDestroyed', {
            playerId: s.id,
            name: s.name,
            survivalTime: s.survivalSeconds
          });
        }
      }

      if (m.y > this.height + 40 || m.x < -40) {
        this.meteors.splice(i, 1);
      }
    }

    // Leaderboard rankings
    const liveLeaderboard = Object.values(this.ships).sort((a, b) => {
      if (a.isAlive && !b.isAlive) return -1;
      if (!a.isAlive && b.isAlive) return 1;
      return b.survivalSeconds - a.survivalSeconds;
    });

    this.io.to(this.room.code).emit('space_state', {
      ships: this.ships,
      meteors: this.meteors,
      elapsed: Math.floor(this.elapsed),
      leaderboard: liveLeaderboard
    });

    if (aliveCount <= 1 && Object.keys(this.ships).length > 1) {
      clearInterval(this.loopInterval);
      setTimeout(() => {
        this.io.to(this.room.code).emit('space_gameOver', {
          winner: lastAlive,
          finalLeaderboard: liveLeaderboard
        });
      }, 1500);
    }
  }

  destroy() {
    if (this.loopInterval) clearInterval(this.loopInterval);
  }
}
